import "../css/hw.css";
import { useEffect, useRef, useState } from "react";
import OfflineCourses from './cources';

function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// Random target between 12500 and 12599 — changes every reload
const TARGET = Math.floor(Math.random() * 100) + 12500;

function useCounter(target, duration = 2200) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const startTime = performance.now();
        const tick = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          // ease out cubic
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(tick);
          else setCount(target);
        };
        requestAnimationFrame(tick);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return { ref, count };
}

const HowItWorks = () => {
  const introRef  = useReveal();
  const certRef   = useReveal();
  const verifyRef = useReveal();
  const statsRef  = useReveal();
  const { ref: counterRef, count } = useCounter(TARGET);

  return (
    <div className="container-fluid">

      <div ref={introRef} className="why-tally-certification text-center reveal">
        <p className="container text-grey mb-5 mt-4">
          Welcome to Shrikant Tally Institute, led by experienced educators and backed by Tally Education. With years of domain expertise, we offer top-tier learning courses and certifications in
          Accounting with TallyPrime. Our programs are designed to sharpen your skills, boost your
          employability, and unlock exciting job opportunities. Let us help you build a successful career!
        </p>
      </div>

      <div ref={certRef} className="row mb-5 reveal">
        <div className="col-lg-5 text-center">
          <img
            src="https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/certificate.jpg"
            alt="Tally Certificate"
            className="img-fluid"
            style={{ width: "100%", maxWidth: "400px", transition: 'transform 0.4s ease, box-shadow 0.4s ease' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
          />
        </div>

        <div className="col-lg-7 my-auto">
          <div className="row mb-4">
            {[
              { src: 'advancement 1.png', alt: 'Career Advancement', title: 'Career Advancement', desc: 'Enhance your career prospects with Tally certification, unlocking opportunities in finance, auditing, and business analysis.' },
              { src: 'our-existence.png',  alt: 'Industry Recognized',  title: 'Industry - Recognized',  desc: 'Master industry-standard accounting practices with Tally certification, ensuring your skills are highly valued by businesses.', h: 40 },
              { src: 'technique 1.png',   alt: 'Comprehensive Skills', title: 'Comprehensive Skills', desc: 'Gain comprehensive skills with our certification, unlocking opportunities in bookkeeping, financial management, and business accounting.', h: 40 },
            ].map((item, i) => (
              <div key={i} className="col-md-4 text-center text-md-start d-flex flex-column align-items-center align-items-md-start mb-4"
                style={{ transition: 'transform 0.3s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = ''}>
                <img src={`https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/${item.src}`} alt={item.alt} height={item.h} className="mb-3" />
                <h6 className="label-bold mb-1 fw-bold">{item.title}</h6>
                <p className="text2">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="d-flex align-items-center gap-3 mb-4">
            <p className="key-offerings m-0 fs-5" style={{ color: "#000000", fontWeight: "bold" }}>Key Offerings</p>
            <div style={{ backgroundColor: "#CECECE", width: "75%", height: "1px" }}></div>
          </div>

          <div className="row">
            {[
              { src: 'tax 1.png',           alt: 'GST Compliance',    title: 'GST Compliance through Simulation', desc: 'Navigate business tax intricacies with hands-on experience in GST compliance, returns, generating e-Way bills, and e-Invoicing etc.' },
              { src: 'webinar 1.png',        alt: 'Offline Internships', title: 'Offline Internships', desc: 'Discover Tally offline internships, aligning with NEP 2020, offering hands-on experiences for real-world success through experiential learning.' },
              { src: 'tally Prime new.png',  alt: 'TallyPrime on AWS',  title: 'TallyPrime on AWS',  desc: 'Experience hands-on training with TallyPrime on AWS, giving learners access to the licensed version of Tally for comprehensive learning.' },
            ].map((item, i) => (
              <div key={i} className="col-md-4 text-center text-md-start d-flex flex-column align-items-center align-items-md-start mb-4"
                style={{ transition: 'transform 0.3s ease' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
                onMouseLeave={e => e.currentTarget.style.transform = ''}>
                <img src={`https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/${item.src}`} alt={item.alt} height={40} className="mb-3" />
                <h6 className="label-bold mb-2 fw-bold">{item.title}</h6>
                <p className="text2">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div ref={verifyRef} className="container-fluid py-4 reveal">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8 bg-light rounded d-flex flex-column flex-md-row align-items-center justify-content-between p-4" style={{ background: "#ecf3d7" }}>
            <div className="d-flex align-items-center mb-3 mb-md-0">
              <img src="https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/info.svg" alt="verify" className="me-3 v1" style={{ height: "40px", width: "40px" }} />
              <span className="fs-6 fw-bold v1" style={{ whiteSpace: "nowrap" }}>If You Already Have A Certificate, Verify It Here</span>
            </div>
            <div className="mx-4 my-2 w-100 d-md-none" style={{ borderTop: "1px solid black" }}></div>
            <hr className="w-25 mx-3 v1" style={{ border: '1px solid black' }} />
            <button
              className="btn text-light fw-bold fs-5 mt-2 mt-md-0"
              style={{ background: '#90b732', minHeight: '50px', width: '100%', maxWidth: '240px', transition: 'transform 0.2s, box-shadow 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(144,183,50,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              Verify Your Certificate
            </button>
          </div>
        </div>
      </div>

      <section ref={statsRef} className="bg-white employer-count mb-5 reveal">
        <div className="d-flex container justify-content-between flex-wrap flex-md-nowrap">
          <div className="d-flex align-items-center justify-content-center">
            <div ref={counterRef} className="employer-count-content p-5" style={{ background: '#ecf3d7', borderRadius: '20px' }}>
              <h2 className="fw-bold mb-4 mb-lg-5 display-2" style={{ color: '#90b732' }}>
                {count.toLocaleString('en-IN')} +
              </h2>
              <h2 className="fw-bold mb-lg-5 display-6">Happy learners</h2>
            </div>
          </div>
          <div className="justify-content-end d-flex mt-5">
            <img src={`${process.env.PUBLIC_URL}/tally.webp`} alt="Tally Jobs" className="d-none d-md-block"
              style={{ borderRadius: '20px', width: '600px', transition: 'transform 0.4s ease' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={e => e.currentTarget.style.transform = ''} />
            <img src="https://tallyeducation.com/tepl/wp-content/themes/TEPL/assets/images/employer2.jpg" alt="Tally Jobs" className="w-100 d-md-none p-3" style={{ borderRadius: '20px', width: '700px' }} />
          </div>
        </div>
      </section>

      <OfflineCourses />

      {/* ── Learning Centre in Damoh ── */}
      <div className="container-fluid py-5" style={{ background: '#f2f8f6' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-md-6">
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#90b732', marginBottom: 8 }}>Find Us</p>
              <h2 className="fw-bold mb-3" style={{ color: '#4d3d6c' }}>Learning Centre in Damoh</h2>
              <p className="text-muted mb-4" style={{ fontSize: 15, lineHeight: 1.8 }}>
                We are conveniently located near Purana Thana, Damoh. Start your career path with Tally certification and expert support — all near you. Search by location, keywords, or Pincode.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-2 mb-3">
                <select className="form-select" style={{ maxWidth: 160, fontSize: 13, borderColor: '#90b732' }}>
                  <option>Damoh</option>
                  <option>Location</option>
                  <option>Center Name</option>
                </select>
                <div className="input-group">
                  <input type="text" className="form-control" defaultValue="Purana Thana, Damoh" placeholder="Enter location or pincode" style={{ fontSize: 13, borderColor: '#90b732' }} />
                  <a
                    href="https://www.google.com/maps/dir//Shrikant+tally+institute,+asati+ward+no.1,+Purana+Thana+Rd,+near+sbi+atm,+Damoh,+Madhya+Pradesh+470661"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                    style={{ background: '#90b732' }}
                  >
                    <i className="bi bi-search text-white"></i>
                  </a>
                </div>
              </div>
              <div className="d-flex align-items-center gap-4 mt-3" style={{ fontSize: 13, color: '#555' }}>
                <span><i className="bi bi-geo-alt-fill me-1" style={{ color: '#90b732' }}></i>Purana Thana, Nearby SBI ATM</span>
                <span><i className="bi bi-telephone-fill me-1" style={{ color: '#90b732' }}></i>9131385180</span>
              </div>
            </div>
            <div className="col-md-6">
              <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 32px rgba(77,61,108,0.12)', border: '2px solid #e8f5c8' }}>
                <iframe
                  title="Shrikant Tally Institute Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.902!2d79.4422!3d23.8360!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39820114bb86174d%3A0xed2dd93d2c2e70b5!2sShrikant%20Tally%20Institute!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
